import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../lib/api/client'
import type { Project } from '../../../lib/api/types'

interface BrandKit {
  id: string
  name: string
  colors: string[]
  fonts: Array<{ family: string; role?: string }>
  isDefault: boolean
}

/**
 * Brand Kit por workspace.
 *
 * É o que faz o Auto Edit entregar o resultado com a identidade do cliente certo — uma
 * agência com dez clientes precisa de dez kits, não de um tema global.
 */
export function BrandKitPanel({ project }: { project: Project }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [colors, setColors] = useState<string[]>(['#FFFFFF', '#E8A33D'])
  const [font, setFont] = useState('Inter')

  const kits = useQuery({
    queryKey: ['brandkits', project.workspaceId],
    queryFn: () => api.get<BrandKit[]>(`/workspaces/${project.workspaceId}/brand-kits`),
  })

  const create = useMutation({
    mutationFn: () =>
      api.post(`/workspaces/${project.workspaceId}/brand-kits`, {
        name,
        colors,
        fonts: [{ family: font, role: 'display' }],
        isDefault: (kits.data?.length ?? 0) === 0,
        captionStyle: { fontFamily: font, color: colors[0], highlightColor: colors[1] },
      }),
    onSuccess: () => {
      setName('')
      void queryClient.invalidateQueries({ queryKey: ['brandkits', project.workspaceId] })
    },
  })

  const apply = useMutation({
    mutationFn: (brandKitId: string) => api.patch(`/projects/${project.id}`, { brandKitId }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['project', project.id] }),
  })

  return (
    <div className="p-3 flex flex-col gap-3">
      <div>
        <h2 className="text-xs font-semibold">Brand Kit</h2>
        <p className="mt-1 text-2xs text-ink-400 leading-relaxed">
          Cores e fontes que a IA usa ao gerar legendas e ao rodar o Auto Edit.
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        {kits.data?.map((kit) => (
          <div
            key={kit.id}
            className={`rounded border p-2.5 ${
              project.brandKitId === kit.id ? 'border-accent bg-accent-soft/20' : 'border-ink-750'
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-2xs font-medium truncate">{kit.name}</span>
              {project.brandKitId === kit.id ? (
                <span className="text-[9px] text-accent shrink-0">em uso</span>
              ) : (
                <button
                  className="text-[9px] text-ink-400 hover:text-accent shrink-0"
                  onClick={() => apply.mutate(kit.id)}
                >
                  usar
                </button>
              )}
            </div>
            <div className="mt-1.5 flex gap-1">
              {kit.colors.map((color, i) => (
                <span
                  key={`${color}-${i}`}
                  className="w-4 h-4 rounded border border-ink-700"
                  style={{ background: color }}
                  title={color}
                />
              ))}
            </div>
            {kit.fonts[0] && (
              <p className="mt-1 text-[9px] text-ink-500">{kit.fonts[0].family}</p>
            )}
          </div>
        ))}
        {kits.data?.length === 0 && (
          <p className="text-2xs text-ink-500">Nenhum kit ainda.</p>
        )}
      </div>

      <form
        className="border-t border-ink-750 pt-3 flex flex-col gap-2"
        onSubmit={(e) => { e.preventDefault(); if (name.trim()) create.mutate() }}
      >
        <label className="flex flex-col gap-1">
          <span className="label">Novo kit</span>
          <input
            id="bk-name"
            className="field"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Cliente A"
          />
        </label>
        <div className="flex gap-2">
          {colors.map((color, index) => (
            <label key={index} className="flex-1 flex flex-col gap-1">
              <span className="label">{index === 0 ? 'Texto' : 'Destaque'}</span>
              <input
                id={`bk-color-${index}`}
                type="color"
                value={color}
                onChange={(e) => {
                  const next = [...colors]
                  next[index] = e.target.value
                  setColors(next)
                }}
                className="h-8 w-full rounded bg-ink-900 border border-ink-700 cursor-pointer"
              />
            </label>
          ))}
        </div>
        <label className="flex flex-col gap-1">
          <span className="label">Fonte</span>
          <input id="bk-font" className="field" value={font} onChange={(e) => setFont(e.target.value)} />
        </label>
        <button type="submit" className="btn-outline justify-center py-2" disabled={create.isPending}>
          {create.isPending ? 'Criando…' : 'Criar kit'}
        </button>
      </form>
    </div>
  )
}
